import { BoardMeetingState } from "@boark/shared/types/board-meeting";
import {
  BoardMeetingTransitionOption,
  canConfirmBoardMeeting,
  canDeleteBoardMeeting,
  canEditBoardMeetingAgenda,
  canEditBoardMeetingAssignments,
  canEditBoardMeetingAttendance,
  canEditBoardMeetingConclusions,
  canEditBoardMeetingDetails,
  getBoardMeetingRoles,
  getBoardMeetingTransitionOptions,
} from "@boark/shared/utils/board-meeting";
import { useState } from "react";
import { Route, Routes, useParams } from "react-router-dom";

import { BoardMeetingAgenda } from "../../components/board-meeting-agenda";
import { BoardMeetingPeople } from "../../components/board-meeting-people";
import { BoardMeetingStateTimeline } from "../../components/board-meeting-state-timeline";
import { Button } from "../../components/button";
import { Confirm } from "../../components/confirm";
import { H1 } from "../../components/h1";
import { H2 } from "../../components/h2";
import { Spinner } from "../../components/spinner";
import {
  useBoardMeetingMutation,
  useGetBoardMeeting,
  useGetBoardMeetingComments,
} from "../../queries/board-meeting";
import { useGetAuthContext } from "../../queries/get-auth-context";
import { changeBoardMeetingState } from "../../services/board-meeting";
import { useBlockedReason } from "../../services/board-meeting-locale";
import { useLocale } from "../../stores/locale";
import { formatDateTime } from "../../utils/date";
import { BoardMeetingDelete } from "./board-meeting-delete";
import { BoardMeetingEdit } from "./board-meeting-edit";

// Transitions that can't be undone and are confirmed first
const CONFIRMED_TRANSITIONS = [
  BoardMeetingState.CALLED,
  BoardMeetingState.ENDED,
  BoardMeetingState.COMPLETE,
] as const;

// Going back in the flow is shown as a secondary action
const BACKWARD_TRANSITIONS: Partial<
  Record<BoardMeetingState, BoardMeetingState>
> = {
  [BoardMeetingState.CALL]: BoardMeetingState.DRAFT,
  [BoardMeetingState.REVIEW]: BoardMeetingState.CLEANING,
};

export function BoardMeetingView() {
  const { id } = useParams();
  const { locale: t } = useLocale();
  const blockedReason = useBlockedReason();

  if (!id) {
    throw new Error("Board meeting ID is required");
  }

  const { data: authContext } = useGetAuthContext();
  const { data: meeting, isLoading } = useGetBoardMeeting(id);
  const { data: comments } = useGetBoardMeetingComments(id);
  const [pendingTransition, setPendingTransition] =
    useState<BoardMeetingState>();

  const changeState = useBoardMeetingMutation(id, (state: BoardMeetingState) =>
    changeBoardMeetingState(id, state),
  );

  if (isLoading) {
    return <Spinner />;
  }
  if (!meeting) {
    return (
      <div className="flex flex-col gap-4 items-start">
        <Button route="/admin/board-meeting/list" severity="secondary">
          {t.boardMeeting.backToList}
        </Button>
      </div>
    );
  }

  const userId = authContext?.id;
  const transitions = getBoardMeetingTransitionOptions(meeting, userId);
  const roles = getBoardMeetingRoles(meeting, userId);
  const canChangeAnything =
    transitions.length > 0 ||
    canEditBoardMeetingDetails(meeting, userId) ||
    canEditBoardMeetingAgenda(meeting, userId) ||
    canEditBoardMeetingAssignments(meeting, userId) ||
    canEditBoardMeetingAttendance(meeting, userId) ||
    canEditBoardMeetingConclusions(meeting, userId) ||
    canConfirmBoardMeeting(meeting, userId);

  const waitingFor =
    meeting.state === BoardMeetingState.CLEANING && !roles.isSecretary
      ? t.boardMeeting.waitingFor.review
      : meeting.state === BoardMeetingState.REVIEW
        ? t.boardMeeting.waitingFor.complete
        : undefined;

  const isPreview =
    meeting.state === BoardMeetingState.CALL ||
    meeting.state === BoardMeetingState.CALLED;

  const transitionLabel = (option: BoardMeetingTransitionOption) =>
    meeting.state === BoardMeetingState.REVIEW &&
    option.state === BoardMeetingState.CLEANING
      ? t.boardMeeting.backToCleaning
      : t.boardMeeting.moveTo[option.state];

  const requestTransition = (state: BoardMeetingState) => {
    if (
      (CONFIRMED_TRANSITIONS as readonly BoardMeetingState[]).includes(state)
    ) {
      setPendingTransition(state);
    } else {
      changeState.mutate(state);
    }
  };

  return (
    <>
      <Routes>
        <Route element={<BoardMeetingEdit meeting={meeting} />} path="edit" />
        <Route element={<BoardMeetingDelete />} path="delete" />
      </Routes>
      <div className="w-full min-h-full flex flex-col gap-6">
        <div className="flex flex-wrap items-center gap-2">
          <Button route="/admin/board-meeting/list" severity="secondary">
            {t.boardMeeting.backToList}
          </Button>
          <div className="flex-1" />
          {canEditBoardMeetingDetails(meeting, userId) && (
            <Button route={`/admin/board-meeting/${id}/edit`}>
              {t.boardMeeting.edit}
            </Button>
          )}
          {canDeleteBoardMeeting(meeting, userId) && (
            <Button
              route={`/admin/board-meeting/${id}/delete`}
              severity="danger"
            >
              {t.action.delete}
            </Button>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <H1>{meeting.name}</H1>
          <p className="m-0 text-gray-700">
            {formatDateTime(meeting.date, false)}
            {" · "}
            {meeting.location || (
              <span className="text-gray-400">
                {t.boardMeeting.locationEmpty}
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-col gap-3 border border-gray-300 rounded-xs p-4 bg-white">
          <BoardMeetingStateTimeline
            state={meeting.state}
            stateChangedAt={meeting.stateChangedAt}
          />
          <p className="m-0 text-sm text-gray-700">
            <span className="font-semibold">
              {t.boardMeeting.states[meeting.state]}:
            </span>{" "}
            {t.boardMeeting.stateDescriptions[meeting.state]}
          </p>
          {transitions.length > 0 && (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {transitions.map((option) => (
                <div key={option.state} className="flex flex-col items-end">
                  <Button
                    disabled={option.blockedBy !== undefined}
                    loading={
                      changeState.isPending &&
                      changeState.variables === option.state
                    }
                    severity={
                      BACKWARD_TRANSITIONS[meeting.state] === option.state
                        ? "secondary"
                        : "primary"
                    }
                    title={
                      option.blockedBy
                        ? blockedReason(option.blockedBy)
                        : undefined
                    }
                    onClick={() => requestTransition(option.state)}
                  >
                    {transitionLabel(option)}
                  </Button>
                  {option.blockedBy && (
                    <span className="text-xs text-amber-700 mt-1">
                      {blockedReason(option.blockedBy)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          {waitingFor && (
            <p className="m-0 text-sm text-gray-500">{waitingFor}</p>
          )}
          {!canChangeAnything && (
            <p className="m-0 text-sm text-gray-500">
              {t.boardMeeting.readOnly}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-6 items-start">
          <div className="flex flex-col gap-3 min-w-0">
            <H2>
              {isPreview ? t.boardMeeting.preview : t.boardMeeting.agenda}
            </H2>
            {isPreview && (
              <p className="m-0 text-sm text-gray-500">
                {t.boardMeeting.previewIntro}
              </p>
            )}
            <BoardMeetingAgenda
              authUserId={userId}
              comments={comments ?? []}
              meeting={meeting}
            />
          </div>
          <div className="flex flex-col gap-3 min-w-0 lg:sticky lg:top-4">
            <H2>{t.boardMeeting.people}</H2>
            <BoardMeetingPeople authUserId={userId} meeting={meeting} />
          </div>
        </div>
      </div>
      {pendingTransition && (
        <Confirm
          header={
            t.boardMeeting.confirmMoveTo[
              pendingTransition as (typeof CONFIRMED_TRANSITIONS)[number]
            ]
          }
          loading={changeState.isPending}
          onClose={() => setPendingTransition(undefined)}
          onConfirm={() => {
            changeState
              .mutateAsync(pendingTransition)
              .finally(() => setPendingTransition(undefined));
          }}
        />
      )}
    </>
  );
}
